import { useState, type ReactElement } from "react";
import { useNavigate } from "react-router";
import type { CommunityDetail, MyCommunity, RailFolder } from "@atrium/shared";
import { useMyCommunities } from "../../lib/hooks";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../../components/ui/ContextMenu";
import { toast } from "../../components/ui/Toast";
import {
  IconCheckRead,
  IconFolderAdd,
  IconFolderMove,
  IconFolderRemove,
  IconLink,
  IconLogout,
  IconSettings,
  IconUngroup,
  IconUserPlus,
} from "../../components/icons";
import { permsOf, useStructure } from "../communities/hooks";
import { InviteDialog } from "../communities/InviteDialog";
import { copyCommunityLink, leaveCommunity, markCommunitiesRead } from "./actions";
import { createFolderWith, folderOf, moveCommunity, removeFromFolder, ungroup } from "./layout";
import { currentRail, folderLabel, saveRail, useRailUI } from "./useRail";

/** Open something (a dialog) once the menu has closed and handed focus back. */
const afterMenu = (fn: () => void) => setTimeout(fn, 0);

function CommunityMenuItems({ community, onInvite }: { community: MyCommunity; onInvite: (c: CommunityDetail) => void }) {
  const navigate = useNavigate();
  const { data: st } = useStructure(community.id);
  const { data: all } = useMyCommunities();
  const perms = st ? permsOf(st.community.myPermissions, st.community.isOwner) : null;
  const layout = currentRail();
  const folder = folderOf(layout, community.id);
  const otherFolders = layout.filter((it): it is RailFolder => it.kind === "folder" && it.id !== folder?.id);
  const names = new Map((all ?? []).map((c) => [c.id, c]));
  return (
    <>
      <ContextMenuItem icon={IconCheckRead} disabled={!community.unread && !community.mentionCount} onSelect={() => void markCommunitiesRead([community.id])}>
        Mark as read
      </ContextMenuItem>
      <ContextMenuSeparator />
      {perms?.invite && (
        <ContextMenuItem icon={IconUserPlus} onSelect={() => st && afterMenu(() => onInvite(st.community))}>
          Invite people
        </ContextMenuItem>
      )}
      {perms?.anyAdmin && (
        <ContextMenuItem icon={IconSettings} onSelect={() => navigate(`/c/${community.id}/settings`)}>
          Community settings
        </ContextMenuItem>
      )}
      <ContextMenuItem icon={IconLink} onSelect={() => void copyCommunityLink(community)}>
        Copy link
      </ContextMenuItem>
      <ContextMenuSeparator />
      {folder ? (
        <ContextMenuItem icon={IconFolderRemove} onSelect={() => saveRail(removeFromFolder(layout, community.id))}>
          Remove from folder
        </ContextMenuItem>
      ) : (
        <ContextMenuItem
          icon={IconFolderAdd}
          onSelect={() => {
            const r = createFolderWith(layout, community.id);
            saveRail(r.items);
            afterMenu(() => useRailUI.getState().setEditing(r.folderId));
          }}
        >
          Create folder
        </ContextMenuItem>
      )}
      {otherFolders.length > 0 && (
        <ContextMenuSub>
          <ContextMenuSubTrigger icon={IconFolderMove}>Move to folder</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {otherFolders.map((f) => (
              <ContextMenuItem key={f.id} onSelect={() => saveRail(moveCommunity(layout, community.id, { folderId: f.id, index: f.communityIds.length }))}>
                {folderLabel(f.name, f.communityIds.map((id) => names.get(id)).filter((c): c is MyCommunity => !!c))}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}
      {st && !st.community.isOwner && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem icon={IconLogout} danger onSelect={() => afterMenu(() => void leaveCommunity(community, navigate))}>
            Leave community
          </ContextMenuItem>
        </>
      )}
    </>
  );
}

/** Right-click (or long-press) menu for a community. `children` is the row element that triggers it. */
export function CommunityContextMenu({ community, children }: { community: MyCommunity; children: ReactElement }) {
  const [invite, setInvite] = useState<CommunityDetail | null>(null);
  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <CommunityMenuItems community={community} onInvite={setInvite} />
        </ContextMenuContent>
      </ContextMenu>
      {invite && <InviteDialog open onOpenChange={(o) => !o && setInvite(null)} community={invite} />}
    </>
  );
}

export function FolderContextMenu({ folder, communities, children }: { folder: RailFolder; communities: MyCommunity[]; children: ReactElement }) {
  const unread = communities.some((c) => c.unread || c.mentionCount > 0);
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem icon={IconSettings} onSelect={() => afterMenu(() => useRailUI.getState().setEditing(folder.id))}>
          Folder settings
        </ContextMenuItem>
        <ContextMenuItem icon={IconCheckRead} disabled={!unread} onSelect={() => void markCommunitiesRead(communities.map((c) => c.id))}>
          Mark all as read
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          icon={IconUngroup}
          onSelect={() => {
            const before = currentRail();
            saveRail(ungroup(before, folder.id));
            toast.show("Folder ungrouped", { label: "Undo", onClick: () => saveRail(before) });
          }}
        >
          Ungroup
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
